import apiClient from '../helpers/apiClient'

export interface CreateExpertisePayload {
  title: string
  description: string
  hourlyRate: number
  scheduleOn: string
}

export const createExpertise = async (partnerId: number | string, payload: CreateExpertisePayload) => {
  const {data} = await apiClient.post(`/expertise/admin/experts/${partnerId}/expertise/`, payload)
  return data
}

// --- Portal del experto ("My Expertise") ------------------------------------------------------
// Ninguno acepta ?partner=: el backend resuelve el partner del usuario en sesion, de modo que un
// experto no puede leer el catalogo de otro cambiando un query param.

export interface MyExpertiseQuery {
  status?: 'pending' | 'active' | 'rejected'
  state?: 'open' | 'closed'
  search?: string
  limit?: number
  offset?: number
}

export const getMyExpertise = async (params: MyExpertiseQuery = {}) => {
  const {data} = await apiClient.get('/expertise/my-expertise/', {params})
  return data
}

export const getMyExpertiseCounters = async () => {
  const {data} = await apiClient.get('/expertise/my-expertise/counters/')
  return data
}

// --- Solicitudes que recibe el experto (/my-expertise) -----------------------------------------
// Proceso distinto al de los servicios: aqui el experto RESPONDE lo que un miembro le pidio.

export type MyRequestTab = 'request' | 'accepted' | 'closed'

export const getMyRequests = async (
  params: {status?: MyRequestTab; limit?: number; offset?: number} = {}
) => {
  const {data} = await apiClient.get('/expertise/my-requests/', {params})
  return data
}

export const getMyRequestCounters = async () => {
  const {data} = await apiClient.get('/expertise/my-requests/counters/')
  return data
}

const requestAction = async (id: number, action: string, body?: any) => {
  const {data} = await apiClient.patch(`/expertise/my-requests/${id}/${action}/`, body ?? {})
  return data
}

export const acceptRequest = (id: number) => requestAction(id, 'accept')
export const declineRequest = (id: number) => requestAction(id, 'decline')
/** Propone otra fecha; la solicitud sigue pendiente y el turno pasa a la otra parte. */
export const rescheduleRequest = (id: number, proposedOn: string) =>
  requestAction(id, 'reschedule', {proposedOn})
/** Confirma la fecha propuesta por la contraparte (accion del chat). */
export const confirmReschedule = (id: number) => requestAction(id, 'confirm')

// --- Timeline de 5 pasos y acciones del trato --------------------------------------------------
// El backend resuelve por completo que puede hacer quien pregunta: cada paso llega con su `state`
// y su lista de `actions`. El front NO decide permisos, solo pinta lo que venga.

export type StepAction =
  | 'reschedule' | 'accept' | 'reject'
  | 'add_contract' | 'skip_contract' | 'remove_contract'
  | 'set_deal_value' | 'confirm_deal' | 'cancel_deal' | 'close_deal'
  /** Solo para el comprador: trato acordado y pendiente de cobro. */
  | 'pay'

export type StepState = 'completed' | 'current' | 'locked' | 'declined' | 'cancelled'

export type StepKey = 'request' | 'response' | 'contract' | 'deal' | 'closed'

export interface StepContract {
  id: number
  name: string
  url: string | null
  uploadedAt: string
  /** El comprador puede adjuntar pero no retirar: le llega en false y la X no se pinta. */
  canRemove: boolean
}

export interface ExpertiseStep {
  key: StepKey
  index: 1 | 2 | 3 | 4 | 5
  title: string
  state: StepState
  completedAt: string | null
  actions: StepAction[]
  data: any
}

export interface StepsResponse {
  perspective: 'expert' | 'client'
  cancellationTerms: string | null
  steps: ExpertiseStep[]
}

export const getRequestSteps = async (id: number): Promise<StepsResponse> => {
  const {data} = await apiClient.get(`/expertise/my-requests/${id}/steps/`)
  return data
}

/** Toda accion responde el timeline COMPLETO: una sola mueve varios pasos a la vez. */
const dealAction = async (id: number, action: string, body?: any): Promise<StepsResponse> => {
  const {data} = await apiClient.post(`/expertise/my-requests/${id}/deal/${action}/`, body ?? {})
  return data
}

export const skipContract = (id: number) => dealAction(id, 'skip-contract')
export const setDealValue = (id: number, amount: number | string) =>
  dealAction(id, 'set-deal-value', {amount})
export const confirmDeal = (id: number) => dealAction(id, 'confirm-deal')
export const cancelDeal = (id: number) => dealAction(id, 'cancel-deal')
export const closeDeal = (id: number) => dealAction(id, 'close-deal')

export const addContract = async (id: number, file: File): Promise<StepsResponse> => {
  const form = new FormData()
  form.append('file', file)
  const {data} = await apiClient.post(`/expertise/my-requests/${id}/deal/add-contract/`, form, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
  return data
}

export const removeContract = async (id: number, contractId: number): Promise<StepsResponse> => {
  const {data} = await apiClient.delete(`/expertise/my-requests/${id}/contracts/${contractId}/`)
  return data
}

// --- Checkout previo al pago (Figma 4457:77327) ---------------------------------------------

export interface ExpertiseCheckout {
  requestId: number
  /** Fila de la negociacion (Engagement): monto acordado y terminos. */
  engagementId: number
  expert: {id: number; companyName: string; logo: string | null}
  service: any
  hours: number
  rate: string | null
  /** tarifa x horas: lo que el comprador pidio */
  subtotal: string | null
  /** monto final del experto menos el subtotal; puede ser negativo */
  adjustment: string | null
  /** lo que se cobra */
  total: string | null
  currency: string
  dealStatus: string
  paymentStatus: string | null
}

export const getCheckout = async (requestId: number): Promise<ExpertiseCheckout> => {
  const {data} = await apiClient.get(`/expertise/my-requests/${requestId}/checkout/`)
  return data
}

/**
 * Cobra el trato. El monto NO se manda: sale del `agreed_amount` del Engagement, para que el
 * comprador no pueda alterarlo desde el navegador.
 *
 * Tres desenlaces: 200 pagado, 202 el banco pide 3D Secure (llega `clientSecret`), 400 rechazado.
 * Axios lanza en 4xx, asi que el 400 se maneja en el catch de quien llama.
 */
export interface PayResult {
  success: boolean
  message?: string
  requiresAction?: boolean
  clientSecret?: string
  paymentMethodId?: string
}

export const payEngagement = async (
  engagementId: number
): Promise<{status: number; data: PayResult}> => {
  const response = await apiClient.post(`/expertise/engagements/${engagementId}/pay/`, {})
  return {status: response.status, data: response.data}
}
